import React, {
  KeyboardEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.scss';

interface ModalProps {
  ariaLabel?: string;
  isModalOpen: boolean;
  allowOuterOverflow?: boolean;
  closeMethods?: ('Keydown' | 'Overlay')[];
  renderContent?: boolean;
  onCloseModal: () => void;
}

const Modal: React.FC<PropsWithChildren<ModalProps>> = ({
  ariaLabel = 'Modal window',
  isModalOpen,
  allowOuterOverflow = false,
  closeMethods = ['Keydown', 'Overlay'],
  renderContent = true,
  onCloseModal,
  children,
}) => {
  const modalBoxRef = useRef<HTMLDivElement>(null);
  const lastFocusedElement = useRef<HTMLElement | null>(null);

  const availableCloseMethods = useMemo(
    () => ({
      keydown: closeMethods.includes('Keydown'),
      overlay: closeMethods.includes('Overlay'),
    }),
    [closeMethods]
  );

  // Manage focus when modal opens or closes
  useEffect(() => {
    if (isModalOpen) {
      lastFocusedElement.current = document.activeElement as HTMLElement;
      modalBoxRef.current?.focus();
    } else if (lastFocusedElement.current) {
      lastFocusedElement.current.focus();
    }

    // Enable or disable scrolling when modal state changes
    document.body.style.overflow = isModalOpen ? 'hidden' : '';
  }, [isModalOpen]);

  // Handle closing the modal based on method (escape key or overlay click)
  const closeModalHandler = useCallback(
    (method: 'keydown' | 'overlay') => {
      if (availableCloseMethods[method]) {
        onCloseModal();
      }
    },
    [availableCloseMethods, onCloseModal]
  );

  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModalHandler('keydown');
      }
    },
    [closeModalHandler]
  );

  if (!renderContent) {
    return null;
  }

  return createPortal(
    <div className={styles['renderer-wrapper']}>
      <div
        ref={modalBoxRef}
        tabIndex={-1}
        role="dialog"
        aria-label={ariaLabel}
        className={`${styles.modal} ${allowOuterOverflow ? styles['modal--overflow'] : ''} ${
          isModalOpen ? styles['fade-enter-active'] : styles['fade-leave-active']
        }`}
        onKeyDown={handleKeydown}
      >
        <div className={`${styles.container} container`}>
          {/* Overlay */}
          <div
            role="button"
            tabIndex={-1}
            aria-label="Close Modal"
            className={`${styles['modal-overlay']} ${
              !availableCloseMethods.overlay ? styles.inactive : ''
            }`}
            onClick={() => closeModalHandler('overlay')}
          />
          {/* Modal Content */}
          <div className={styles['modal-main']}>{children}</div>
        </div>
      </div>
    </div>,
    document.body // Appends the modal into the portal at the root
  );
};

export default Modal;
